import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import { createDefaultBinding, createDefaultElement, createDefaultHmiDocument } from "../utils/default-document";

export type HmiTemplateDefinition = {
  id: string;
  title: string;
  description: string;
  category: "overview" | "process" | "utility";
  buildDocument: () => HmiCanvasDocument;
};

function placeElement<T extends HmiElement>(element: T, patch: Partial<T>): T {
  return { ...element, ...patch };
}

function withBinding(document: HmiCanvasDocument, element: HmiElement) {
  const binding = createDefaultBinding(element);
  return {
    ...document,
    elements: [...document.elements, element],
    bindings: binding ? [...document.bindings, binding] : document.bindings,
  };
}

function buildOverviewTemplate(): HmiCanvasDocument {
  let document = createDefaultHmiDocument();
  document.canvas.width = 1760;
  document.canvas.height = 980;
  document.metadata.workflow = {
    publicationStatus: "draft",
    templateId: "template.overview.operations",
    templateName: "Operations Overview",
  };

  document = withBinding(document, placeElement(createDefaultElement("stat_card", 1), { x: 80, y: 72, width: 260, height: 120, label: "Flow Rate", name: "Flow KPI" }));
  document = withBinding(document, placeElement(createDefaultElement("gauge", 2), { x: 96, y: 260, width: 240, height: 240, name: "Tank Pressure" }));
  document = withBinding(document, placeElement(createDefaultElement("trend_chart", 3), { x: 420, y: 72, width: 620, height: 260, name: "Line Trend" }));
  document = withBinding(document, placeElement(createDefaultElement("data_table", 4), { x: 420, y: 364, width: 620, height: 252, name: "Recent Readings" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 5, { symbolDefinitionId: "builtin.process.tank" }), { x: 1120, y: 120, width: 220, height: 320, name: "Storage Tank" }));
  document = withBinding(document, placeElement(createDefaultElement("command_toggle", 6), { x: 1100, y: 508, width: 260, height: 96, name: "Pump Toggle" }));
  document = withBinding(document, placeElement(createDefaultElement("setpoint", 7), { x: 1100, y: 632, width: 280, height: 110, name: "Setpoint Control" }));
  document.elements.push(placeElement(createDefaultElement("text", 8), { x: 80, y: 20, width: 520, height: 36, text: "Operations Overview", name: "Title" }));
  return document;
}

function buildProcessSkidTemplate(): HmiCanvasDocument {
  let document = createDefaultHmiDocument();
  document.canvas.width = 1680;
  document.canvas.height = 900;
  document.metadata.workflow = {
    publicationStatus: "draft",
    templateId: "template.process.skid",
    templateName: "Process Skid",
  };
  document.elements.push(placeElement(createDefaultElement("text", 1), { x: 72, y: 24, width: 420, height: 32, text: "Process Skid", name: "Title" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 2, { symbolDefinitionId: "builtin.process.pipe" }), { x: 96, y: 216, width: 240, height: 48, name: "Inlet Pipe" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 3, { symbolDefinitionId: "builtin.process.valve" }), { x: 344, y: 176, width: 180, height: 120, name: "Isolation Valve" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 4, { symbolDefinitionId: "builtin.process.pump" }), { x: 560, y: 156, width: 240, height: 150, name: "Transfer Pump" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 5, { symbolDefinitionId: "builtin.process.tank" }), { x: 944, y: 96, width: 240, height: 320, name: "Blending Tank" }));
  document = withBinding(document, placeElement(createDefaultElement("value", 6), { x: 560, y: 344, width: 180, height: 78, name: "Pump Amps" }));
  document = withBinding(document, placeElement(createDefaultElement("trend_chart", 7), { x: 96, y: 500, width: 620, height: 220, name: "Skid Trend" }));
  document = withBinding(document, placeElement(createDefaultElement("command_button", 8), { x: 944, y: 456, width: 220, height: 64, name: "Start Batch" }));
  document = withBinding(document, placeElement(createDefaultElement("command_toggle", 9), { x: 944, y: 556, width: 260, height: 90, name: "Pump State" }));
  return document;
}

function buildUtilityTemplate(): HmiCanvasDocument {
  let document = createDefaultHmiDocument();
  document.canvas.width = 1600;
  document.canvas.height = 920;
  document.metadata.workflow = {
    publicationStatus: "draft",
    templateId: "template.utility.air-handler",
    templateName: "Utility Air Handler",
  };
  document.elements.push(placeElement(createDefaultElement("text", 1), { x: 64, y: 22, width: 420, height: 32, text: "Air Handler Utility Screen", name: "Title" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 2, { symbolDefinitionId: "builtin.hvac.duct" }), { x: 144, y: 182, width: 360, height: 86, name: "Supply Duct" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 3, { symbolDefinitionId: "builtin.hvac.fan" }), { x: 548, y: 126, width: 180, height: 180, name: "Supply Fan" }));
  document = withBinding(document, placeElement(createDefaultElement("symbol_instance", 4, { symbolDefinitionId: "builtin.hvac.damper" }), { x: 790, y: 164, width: 240, height: 120, name: "Outdoor Damper" }));
  document = withBinding(document, placeElement(createDefaultElement("stat_card", 5), { x: 1180, y: 120, width: 250, height: 120, label: "Temp", name: "Supply Air Temp" }));
  document = withBinding(document, placeElement(createDefaultElement("gauge", 6), { x: 1158, y: 302, width: 250, height: 250, name: "Static Pressure" }));
  document = withBinding(document, placeElement(createDefaultElement("command_toggle", 7), { x: 560, y: 364, width: 220, height: 88, name: "Fan Enable" }));
  document = withBinding(document, placeElement(createDefaultElement("setpoint", 8), { x: 818, y: 360, width: 260, height: 100, name: "SAT Setpoint" }));
  document = withBinding(document, placeElement(createDefaultElement("trend_chart", 9), { x: 108, y: 540, width: 760, height: 220, name: "AHU Trend" }));
  return document;
}

export const hmiTemplates: HmiTemplateDefinition[] = [
  {
    id: "template.overview.operations",
    title: "Operations Overview",
    description: "High-level tank, trend, and operator controls for a supervisor screen.",
    category: "overview",
    buildDocument: buildOverviewTemplate,
  },
  {
    id: "template.process.skid",
    title: "Process Skid",
    description: "Inline valve, pump, tank, and batch controls for a compact process skid.",
    category: "process",
    buildDocument: buildProcessSkidTemplate,
  },
  {
    id: "template.utility.air-handler",
    title: "Utility Air Handler",
    description: "AHU-focused starter screen with fan, damper, setpoint, and utility telemetry.",
    category: "utility",
    buildDocument: buildUtilityTemplate,
  },
];

export function getHmiTemplateById(templateId: string): HmiTemplateDefinition | undefined {
  return hmiTemplates.find((template) => template.id === templateId);
}
